import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  ShoppingBagIcon,
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TruckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  UserIcon,
  InformationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  ShoppingBagIcon as ShoppingBagIconSolid,
  ArrowsRightLeftIcon as ArrowsRightLeftIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  TruckIcon as TruckIconSolid,
  ClockIcon as ClockIconSolid,
  ClipboardDocumentCheckIcon as ClipboardCheckSolid
} from '@heroicons/react/24/solid';
import { ordersAPI, getImageUrl } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

const AdminAllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Filters state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Load orders
  useEffect(() => {
    loadOrders();
  }, [filters]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: filters.page,
        limit: filters.limit,
        search: filters.search || undefined,
        status: filters.status || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await ordersAPI.getAll(params);
      setOrders(response.data.data.orders);
      setPagination(response.data.data.pagination);
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  // Handle view order details
  const handleViewOrder = async (order) => {
    try {
      const response = await ordersAPI.getById(order._id);
      setSelectedOrder(response.data.data);
      setShowDetailsModal(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تحميل تفاصيل الطلب');
    }
  };

  // Get status text
  const getStatusText = (status) => {
    const statusTexts = {
      pending: 'في الانتظار',
      paid: 'تم الدفع',
      admin_received: 'تم الاستلام',
      in_transit: 'في الطريق',
      delivered: 'تم التسليم',
      completed: 'مكتمل',
      cancelled: 'ملغي',
      refunded: 'مُسترد'
    };
    return statusTexts[status] || status;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'yellow', text: 'في الانتظار', icon: ClockIcon },
      paid: { color: 'blue', text: 'تم الدفع', icon: ClockIconSolid },
      admin_received: { color: 'indigo', text: 'تم الاستلام', icon: ClipboardCheckSolid },
      in_transit: { color: 'purple', text: 'في الطريق', icon: TruckIconSolid },
      delivered: { color: 'emerald', text: 'تم التسليم', icon: CheckCircleIconSolid },
      completed: { color: 'green', text: 'مكتمل', icon: CheckCircleIconSolid },
      cancelled: { color: 'red', text: 'ملغي', icon: XCircleIcon },
      refunded: { color: 'gray', text: 'مُسترد', icon: ArrowPathIcon }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 ml-1" />
        {config.text}
      </span>
    );
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", damping: 20, stiffness: 300 }
    }
  };

  if (loading && orders.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ShoppingBagIcon className="h-8 w-8 ml-3 text-blue-600" />
                سجل جميع الطلبات
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                عرض وتتبع جميع الطلبات في النظام مع إمكانية البحث والفلترة
              </p>
            </div>
            <button
              onClick={loadOrders}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowPathIcon className="h-5 w-5 ml-2" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6"
      >
        <motion.div 
          variants={itemVariants}
          whileHover={{ scale: 1.02, y: -5 }}
          className="relative overflow-hidden bg-white rounded-xl shadow-lg border border-gray-100"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue to-brand-blueSecondary opacity-5"></div>
          <div className="relative p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">إجمالي الطلبات</p>
                <p className="text-3xl font-bold text-gray-900 mb-2">{pagination.total || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blueSecondary">
                <ShoppingBagIconSolid className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          variants={itemVariants}
          whileHover={{ scale: 1.02, y: -5 }}
          className="relative overflow-hidden bg-white rounded-xl shadow-lg border border-gray-100"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-green-600 opacity-5"></div>
          <div className="relative p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">طلبات مكتملة</p>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {orders.filter(o => ['delivered', 'completed'].includes(o.status)).length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-r from-green-500 to-green-600">
                <CheckCircleIconSolid className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          variants={itemVariants}
          whileHover={{ scale: 1.02, y: -5 }}
          className="relative overflow-hidden bg-white rounded-xl shadow-lg border border-gray-100"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-amber-600 opacity-5"></div>
          <div className="relative p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">قيد التنفيذ</p>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {orders.filter(o => ['paid', 'admin_received', 'in_transit'].includes(o.status)).length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600">
                <TruckIconSolid className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          variants={itemVariants}
          whileHover={{ scale: 1.02, y: -5 }}
          className="relative overflow-hidden bg-white rounded-xl shadow-lg border border-gray-100"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 opacity-5"></div>
          <div className="relative p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">ملغية</p>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {orders.filter(o => o.status === 'cancelled').length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-r from-red-500 to-red-600">
                <XCircleIcon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم الطلب..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">جميع الحالات</option>
              <option value="pending">في الانتظار</option>
              <option value="paid">تم الدفع</option>
              <option value="admin_received">تم الاستلام</option>
              <option value="in_transit">في الطريق</option>
              <option value="delivered">تم التسليم</option>
              <option value="completed">مكتمل</option>
              <option value="cancelled">ملغي</option>
            </select>

            {/* Sort By */}
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="createdAt">تاريخ الإنشاء</option>
              <option value="updatedAt">تاريخ التحديث</option>
              <option value="status">الحالة</option>
              <option value="totalAmount">المبلغ</option>
            </select>

            {/* Sort Order */}
            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="desc">الأحدث أولاً</option>
              <option value="asc">الأقدم أولاً</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="mr-3 text-gray-600">جاري التحميل...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد طلبات</h3>
            <p className="mt-1 text-sm text-gray-500">لا توجد طلبات تطابق المعايير المحددة</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      رقم الطلب
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المشتري
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      البائع
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المبلغ
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الحالة
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      تاريخ الإنشاء
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <motion.tr
                      key={order._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.orderNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.buyer?.lab?.labName}</div>
                        <div className="text-sm text-gray-500">{order.buyer?.user?.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.seller?.lab?.labName}</div>
                        <div className="text-sm text-gray-500">{order.seller?.user?.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.totalAmount?.toLocaleString()} {order.currency || 'SAR'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.createdAt).toLocaleDateString('ar-SA')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="عرض التفاصيل"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handleFilterChange('page', Math.max(1, pagination.page - 1))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    السابق
                  </button>
                  <button
                    onClick={() => handleFilterChange('page', Math.min(pagination.pages, pagination.page + 1))}
                    disabled={pagination.page === pagination.pages}
                    className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    التالي
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      عرض{' '}
                      <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                      {' '}إلى{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>
                      {' '}من{' '}
                      <span className="font-medium">{pagination.total}</span>
                      {' '}نتيجة
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handleFilterChange('page', Math.max(1, pagination.page - 1))}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        السابق
                      </button>
                      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handleFilterChange('page', page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.page
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handleFilterChange('page', Math.min(pagination.pages, pagination.page + 1))}
                        disabled={pagination.page === pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        التالي
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-br from-brand-blue via-brand-blue to-brand-blueSecondary text-white p-6 rounded-t-xl relative overflow-hidden">
                <div className="absolute inset-0 bg-white opacity-5">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    <pattern id="pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                      <circle cx="20" cy="20" r="1" fill="currentColor" />
                    </pattern>
                    <rect width="100%" height="100%" fill="url(#pattern)" />
                  </svg>
                </div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center">
                      <ShoppingBagIconSolid className="h-6 w-6 ml-2" />
                      تفاصيل الطلب
                    </h2>
                    <p className="text-blue-100 mt-1">
                      {selectedOrder.orderNumber}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <InformationCircleIcon className="h-5 w-5 ml-2 text-blue-600" />
                      معلومات أساسية
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الطلب:</span>
                        <span className="font-medium">{selectedOrder.orderNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">الحالة:</span>
                        {getStatusBadge(selectedOrder.status)}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ الإنشاء:</span>
                        <span className="font-medium">
                          {new Date(selectedOrder.createdAt).toLocaleDateString('ar-SA')}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">المبلغ الإجمالي:</span>
                        <span className="font-medium text-green-600">
                          {selectedOrder.totalAmount?.toLocaleString()} ريال
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <TruckIcon className="h-5 w-5 ml-2 text-blue-600" />
                      معلومات الشحن
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">شركة الشحن:</span>
                        <span className="font-medium">
                          {selectedOrder.shipping?.carrier || 'غير محدد'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم التتبع:</span>
                        <span className="font-medium">
                          {selectedOrder.shipping?.trackingNumber || 'غير محدد'}
                        </span>
                      </div>
                      {selectedOrder.shipping?.pickupCompletedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">تاريخ الاستلام:</span>
                          <span className="font-medium">
                            {new Date(selectedOrder.shipping.pickupCompletedAt).toLocaleDateString('ar-SA')}
                          </span>
                        </div>
                      )}
                      {selectedOrder.shipping?.deliveryCompletedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">تاريخ التسليم:</span>
                          <span className="font-medium">
                            {new Date(selectedOrder.shipping.deliveryCompletedAt).toLocaleDateString('ar-SA')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pickup Address (Seller) */}
                {selectedOrder.seller?.lab && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MapPinIcon className="h-5 w-5 ml-2 text-emerald-600" />
                      عنوان الاستلام (البائع)
                    </h3>
                    <div className="bg-emerald-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center">
                          <BuildingStorefrontIcon className="h-4 w-4 ml-2 text-gray-400" />
                          <span className="font-medium">{selectedOrder.seller.lab.labName}</span>
                        </div>
                        {selectedOrder.seller?.user?.phone && (
                          <div className="flex items-center">
                            <PhoneIcon className="h-4 w-4 ml-2 text-gray-400" />
                            <span>{selectedOrder.seller.user.phone}</span>
                          </div>
                        )}
                        {selectedOrder.seller?.user?.email && (
                          <div className="flex items-center">
                            <EnvelopeIcon className="h-4 w-4 ml-2 text-gray-400" />
                            <span>{selectedOrder.seller.user.email}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-1">
                        <span className="text-gray-600">العنوان:</span>
                        <p className="text-gray-800 mt-1">{selectedOrder.seller.lab.address}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delivery Address (Buyer) */}
                {selectedOrder.shippingAddress && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MapPinIcon className="h-5 w-5 ml-2 text-amber-600" />
                      عنوان التسليم (المشتري)
                    </h3>
                    <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">الاسم الكامل:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.fullName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">الهاتف:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.phone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">المدينة:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.city}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">المنطقة:</span>
                          <span className="font-medium">{selectedOrder.shippingAddress.region}</span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <span className="text-gray-600">العنوان الكامل:</span>
                        <p className="text-gray-800 mt-1">{selectedOrder.shippingAddress.address}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Items */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 ml-2 text-indigo-600" />
                    المنتجات
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      {selectedOrder.items?.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                          <div className="flex items-center space-x-3 space-x-reverse">
                            {item.productImage && (
                              <img
                                src={getImageUrl(item.productImage)}
                                alt={item.productName}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            )}
                            <div>
                              <h4 className="font-medium text-gray-900">{item.productName}</h4>
                              <p className="text-sm text-gray-500">الكمية: {item.quantity}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">
                              {item.total?.toLocaleString()} ريال
                            </p>
                            <p className="text-sm text-gray-500">
                              {item.price?.toLocaleString()} × {item.quantity}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status History */}
                {selectedOrder.statusHistory && selectedOrder.statusHistory.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 ml-2 text-purple-600" />
                      سجل الحالات
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2">
                        {selectedOrder.statusHistory.map((history, index) => (
                          <div key={index} className="flex items-center justify-between p-2">
                            <div className="flex items-center space-x-2 space-x-reverse">
                              {getStatusBadge(history.status)}
                              <span className="text-sm text-gray-600">
                                {new Date(history.updatedAt).toLocaleString('ar-SA')}
                              </span>
                            </div>
                            {history.note && (
                              <span className="text-sm text-gray-500">{history.note}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-xl flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAllOrders;